import React, {
  MouseEvent,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from 'react';
import ReactDOM from 'react-dom';
import {
  Placement,
  arrow,
  autoUpdate,
  computePosition,
  flip,
  hide,
  shift,
  size,
} from '@floating-ui/react';
import styled from 'astroturf/react';

import useOnClickOutside from 'js/utils/hooks/useOnClickOutside';
// This import is in JSDoc.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Menu } from './Menu';

import { ModalElementContext } from './Modal';

const ActivatorContainer = styled.div<{ inline?: boolean }>`
  &.inline {
    display: inline;
  }
`;

const PopoverContainer = styled.div<{ strategy: 'absolute' | 'fixed' }>`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  width: max-content;

  // Popovers have preference over modals and most other things.
  z-index: 100;

  &.strategy-fixed {
    position: fixed;
  }
`;

export enum PopoverMode {
  Click = 'click',
  Hover = 'hover',
  Any = 'any',
}

export enum PopoverWidthMode {
  Activator = 'activator',
  Content = 'content',
}

export type PopoverProps = {
  /**
   * Controlled component toggle. Determines if the popover should currently show.
   */
  open?: boolean;
  /**
   * Uncontrolled component options.
   * {@link PopoverMode.Click} shows the popover after clicking the activator.
   * {@link PopoverMode.Hover} shows the popover when hovering over the activator.
   * {@link PopoverMode.Any} shows the popover after clicking or hovering the activator.
   */
  mode?: PopoverMode;
  /**
   * How the popover should determine its size.
   * {@link PopoverWidthMode.Activator} match the width of the activator.
   * {@link PopoverWidthMode.Content} match the width of the content.
   */
  widthMode?: PopoverWidthMode;
  /**
   * Where the popover should by default, relative to the activator.
   * See {@link Placement} for more details.
   */
  placement?: Placement;
  /**
   * Convenience prop to keep the activator inline with the rest of the content.
   */
  inline?: boolean;
  /**
   * How far away the popover should be from the activator in pixels.
   */
  offset?: number;
  /**
   * Whether to allow the popover to show.
   */
  disabled?: boolean;
  /**
   * The maximum width for the popover, in pixels.
   */
  maxWidth?: number;
  /**
   * The maximum height for the popover, in pixels.
   */
  maxHeight?: number;
  /**
   * Whether the popover should close when clicked. This option is useful
   * if you want the menu to close only when clicking outside.
   */
  closeOnClick?: boolean;
  /**
   * Reference to the popover.
   */
  popoverRef?: MutableRefObject<HTMLDivElement | null>;
  /**
   * Class names for the activator.
   */
  className?: string;
  /**
   * Styles for the activator.
   */
  style?: React.CSSProperties;
  /**
   * Class names for the popover container.
   */
  containerClassName?: string;
  /**
   * Styles for the popover container.
   */
  containerStyle?: React.CSSProperties;
  /**
   * The popover activator. Depending on the popover mode,
   * the popover will appear when hovering/clicking on these children.
   */
  children: ReactNode;
  /**
   * The element to render the popover in. It's usually fine to
   * leave this out and just use document.body, but you might need
   * this to render inside of a modal or other container.
   */
  portalBasis?: HTMLElement | null;
  /**
   * Render callback for the popover. A callback is better than
   * a simple {@link ReactNode} because it allows lazy rendering
   * if the popover content is complex or fetches data.
   */
  render: () => ReactNode;
  /**
   * Callback when the menu changes open state.
   * @param open The updated open state.
   */
  onChange?: (open: boolean) => void;
  /**
   * Disable automatic flipping to keep popover in viewport.
   */
  disableFlip?: boolean;
  /**
   * Disable automatic shifting to keep popover in viewport.
   */
  disableShift?: boolean;
  strategy?: 'absolute' | 'fixed';
  /**
   * Ref to an arrow/nubbin element. When provided, the popover uses
   * floating-ui's `arrow` middleware to position the element so that it
   * tracks the activator's center along the cross axis. The element must
   * be a positioned descendant of the popover (e.g. `position: absolute`)
   * for the computed `top`/`left` values to apply.
   *
   * After each position update, the popover also writes the resolved
   * placement (after `flip`) to the arrow element's `data-placement`
   * attribute so consumers can style its visual side via CSS.
   */
  arrowRef?: MutableRefObject<HTMLElement | null>;
  /**
   * Padding (in pixels) that prevents the arrow from being placed within
   * `arrowPadding` pixels of the popover's edges. Useful when the popover
   * has rounded corners that the arrow shouldn't bleed into.
   */
  arrowPadding?: number;
};

/**
 * A basic popover component with no opinionated styling.
 * It provides core popover functionality including placement,
 * common open/close mechanisms, and keeping the popover in the viewport.
 *
 * If you want to use a popover with Academia styles, see {@link Menu} instead.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Popover, PopoverMode } from 'js/design_system/Popover';
 *
 * <Popover
 *   mode={PopoverMode.Click}
 *   placement='bottom-start'
 *   offset={4}
 *   render={() => <p>I'm the popover content!</p>}
 * >
 *   <p>Click me to activate!</p>
 * </Popover>
 * ```
 */
export const Popover: React.FC<PopoverProps> = ({
  open: controlledOpen,
  mode = PopoverMode.Hover,
  widthMode = PopoverWidthMode.Content,
  placement = 'bottom',
  inline = false,
  offset = 0,
  disabled = false,
  disableFlip = false,
  disableShift = false,
  strategy = 'absolute',
  maxWidth,
  maxHeight,
  closeOnClick = false,
  popoverRef: propPopoverRef,
  className = '',
  style,
  containerClassName = '',
  containerStyle,
  portalBasis,
  children,
  render,
  onChange = () => null,
  arrowRef,
  arrowPadding,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const activatorRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const controlled = controlledOpen !== undefined;
  const modalElement = useContext(ModalElementContext)?.current;

  useEffect(() => {
    setIsClient(true);
  }, []);

  const handleClick =
    !controlled && (mode === PopoverMode.Click || mode === PopoverMode.Any);
  const handleHover =
    !controlled && (mode === PopoverMode.Hover || mode === PopoverMode.Any);

  const open = useMemo(() => {
    return controlledOpen ?? uncontrolledOpen;
  }, [controlledOpen, uncontrolledOpen]);

  const updatePosition = useCallback(async () => {
    if (!activatorRef.current || !popoverRef.current) return;

    const arrowElement = arrowRef?.current ?? null;
    const middleware = [
      ...(!disableFlip ? [flip()] : []),
      ...(!disableShift ? [shift({ crossAxis: true })] : []),
      size({
        apply({ availableWidth, availableHeight, rects }) {
          if (!popoverRef.current) return;

          const finalMaxWidth = Math.min(
            availableWidth,
            maxWidth ?? availableWidth,
          );
          const finalMaxHeight = Math.min(
            availableHeight,
            maxHeight ?? availableHeight,
          );
          Object.assign(popoverRef.current.style, {
            maxWidth: `${finalMaxWidth}px`,
            maxHeight: `${finalMaxHeight}px`,
          });
          if (widthMode === PopoverWidthMode.Activator) {
            const side = placement.split('-')[0];
            const width = ['top', 'bottom'].includes(side)
              ? `${rects.reference.width}px`
              : undefined;
            const height = ['left', 'right'].includes(side)
              ? `${rects.reference.height}px`
              : undefined;
            Object.assign(popoverRef.current.style, { width, height });
          }
        },
      }),
      ...(arrowElement
        ? [arrow({ element: arrowElement, padding: arrowPadding })]
        : []),
      // Must run last so it sees the final position. Without it, `shift`/`flip`
      // keep an open popover inside the viewport after its activator has
      // scrolled out of view, so the popover appears to detach and follow the
      // scroll (e.g. an always-open nav tooltip pinned to the top edge).
      hide(),
    ];
    const {
      x,
      y,
      placement: resolvedPlacement,
      middlewareData,
    } = await computePosition(activatorRef.current, popoverRef.current, {
      placement,
      strategy,
      middleware,
    });
    // due to the async call above, we need to check if the refs are still valid
    if (!popoverRef.current) return;
    Object.assign(popoverRef.current.style, {
      top: `${y}px`,
      left: `${x}px`,
      visibility: middlewareData.hide?.referenceHidden ? 'hidden' : '',
    });

    if (arrowElement && middlewareData.arrow) {
      const { x: arrowX, y: arrowY } = middlewareData.arrow;
      Object.assign(arrowElement.style, {
        left: arrowX != null ? `${arrowX}px` : '',
        top: arrowY != null ? `${arrowY}px` : '',
      });
      arrowElement.dataset.placement = resolvedPlacement;
    }
  }, [
    placement,
    strategy,
    widthMode,
    maxWidth,
    maxHeight,
    disableFlip,
    disableShift,
    arrowRef,
    arrowPadding,
  ]);

  const updateOpen = useCallback(
    (update: boolean) => {
      setUncontrolledOpen(!disabled && update);
      onChange(!disabled && update);
    },
    [disabled, onChange],
  );

  // Only an OPEN popover may dismiss on an outside press. Without the guard,
  // every closed Popover on the page reports onChange(false) for presses that
  // land inside a sibling popover's panel — a parent sharing one open-menu
  // state across several popovers then closes the open menu mid-click.
  useOnClickOutside([activatorRef, popoverRef], () => {
    if (open) {
      updateOpen(false);
    }
  });

  // A click into an iframe (e.g. an embedded mini-app canvas) never surfaces as
  // a document-level mousedown, so useOnClickOutside above can't see it and the
  // popover would stay open. Detect focus moving into an iframe via the window
  // `blur` event and close — unless that iframe lives inside the popover itself.
  useEffect(() => {
    if (!open) return undefined;
    const handleWindowBlur = () => {
      // On blur, document.activeElement hasn't updated to the iframe yet, so
      // defer the check to the next tick.
      window.setTimeout(() => {
        const active = document.activeElement;
        if (
          active instanceof HTMLIFrameElement &&
          !popoverRef.current?.contains(active)
        ) {
          updateOpen(false);
        }
      }, 0);
    };
    window.addEventListener('blur', handleWindowBlur);
    return () => window.removeEventListener('blur', handleWindowBlur);
  }, [open, updateOpen]);

  useEffect(() => {
    if (activatorRef.current && popoverRef.current) {
      return autoUpdate(
        activatorRef.current,
        popoverRef.current,
        updatePosition,
      );
    }
    // isClient controls when the portal (and thus popoverRef) becomes available.
    // Without it, opening the popover before isClient=true (e.g. via autoFocus)
    // leaves autoUpdate unregistered because the effect won't re-run once the
    // portal mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, updatePosition, isClient]);

  useEffect(() => {
    if (disabled && open) {
      updateOpen(false);
    }
  }, [disabled, open, updateOpen]);

  const offsetStyle = useMemo((): React.CSSProperties => {
    const side = placement.split('-')[0];
    const paddingSide =
      {
        top: 'paddingBottom',
        right: 'paddingLeft',
        bottom: 'paddingTop',
        left: 'paddingRight',
      }[side] || 'paddingTop';
    return { [paddingSide]: offset };
  }, [placement, offset]);

  const handleActivatorClick = () => {
    if (handleClick) {
      updateOpen(!open);
    }
  };

  const handlePopoverClick = (event: MouseEvent) => {
    event.stopPropagation();
    if (closeOnClick) {
      updateOpen(false);
    }
  };

  const handleMouseEnter = () => {
    if (handleHover) {
      updateOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (handleHover) {
      updateOpen(false);
    }
  };

  return (
    <ActivatorContainer
      ref={activatorRef}
      inline={inline}
      className={className}
      style={style}
      onClick={handleActivatorClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}

      {isClient &&
        ReactDOM.createPortal(
          <PopoverContainer
            strategy={strategy}
            ref={(ref) => {
              popoverRef.current = ref;
              if (propPopoverRef) {
                propPopoverRef.current = ref;
              }
            }}
            className={containerClassName}
            style={{ ...containerStyle, ...offsetStyle }}
            onClick={handlePopoverClick}
          >
            {open && render()}
          </PopoverContainer>,
          portalBasis ?? modalElement ?? document.body,
        )}
    </ActivatorContainer>
  );
};
